import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AirplaneTilt, SpinnerGap, User, CalendarBlank, IdentificationCard, Globe,
  Briefcase, MapPin, CurrencyEur, EnvelopeSimple, GenderMale
} from '@phosphor-icons/react';
import { TemplateSelector, FormField, DateField, HistoryList, HistoryPanel } from './FormFields';

export const InvitationVisaTab = ({
  invitationTemplates, selectedInvitationTemplateId, setSelectedInvitationTemplateId,
  visaTemplates, selectedVisaTemplateId, setSelectedVisaTemplateId,
  invitationForm, setInvitationForm,
  isGeneratingInvitation, isAdmin,
  invitations, loadingInvitations, visas,
  onGenerate, onDownloadInvitation, onDeleteInvitation,
  onDownloadVisa, onDeleteVisa
}) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-2">
      <div className="border border-[#262626] bg-[#141414]">
        <div className="p-4 border-b border-[#262626]">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Приглашение / Invitation</h2>
          <p className="text-xs text-[#525252] mt-1">Выберите шаблон и заполните данные. Штрих-код генерируется автоматически.</p>
        </div>
        <div className="p-4 space-y-4">
          <TemplateSelector templates={invitationTemplates} selectedId={selectedInvitationTemplateId}
            onSelect={setSelectedInvitationTemplateId} testId="invitation-template-select" isAdmin={isAdmin} />

          {visaTemplates.length > 0 && (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3] mb-2">Шаблон визы (опционально)</p>
              <Select value={selectedVisaTemplateId} onValueChange={setSelectedVisaTemplateId}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] rounded-none h-11 focus:border-[#FACC15] focus:ring-0" data-testid="visa-template-select">
                  <SelectValue placeholder="Выберите шаблон визы" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626] text-[#FAFAFA] rounded-none">
                  <SelectItem value="none" className="focus:bg-[#FACC15]/10 rounded-none">Без визы</SelectItem>
                  {visaTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id} className="focus:bg-[#FACC15]/10 rounded-none">{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {invitationTemplates.length > 0 && (
            <>
              {/* Visa Type selector - only for templates with Tier/Category (England) */}
              {(() => {
                const sel = invitationTemplates.find(t => t.id === selectedInvitationTemplateId);
                return sel?.original_fields?.category;
              })() && (
                <div className="border-t border-[#262626] pt-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FACC15] mb-3">Тип визы</p>
                  <Select value={invitationForm.visaType} onValueChange={(v) => setInvitationForm(p => ({...p, visaType: v}))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] rounded-none h-11 focus:border-[#FACC15] focus:ring-0" data-testid="inv-visa-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626] text-[#FAFAFA] rounded-none">
                      <SelectItem value="Tier 2 (General worker)" className="focus:bg-[#FACC15]/10 rounded-none">Tier 2 (General worker)</SelectItem>
                      <SelectItem value="Tier 5 (General worker)" className="focus:bg-[#FACC15]/10 rounded-none">Tier 5 (General worker)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Sponsor section */}
              <div className="border-t border-[#262626] pt-4">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FACC15] mb-3">Спонсор</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField icon={Briefcase} label="Sponsor Name" value={invitationForm.sponsorName}
                    onChange={(v) => setInvitationForm(p => ({...p, sponsorName: v}))} placeholder="DHL EXPRESS DEUTSCHLAND GMBH" testId="inv-sponsor-name" />
                  <FormField icon={IdentificationCard} label="Certificate Number" value={invitationForm.certificateNumber}
                    onChange={(v) => setInvitationForm(p => ({...p, certificateNumber: v}))} placeholder="C5S1F68384H" testId="inv-cert-number" />
                </div>
              </div>

              {/* Dates section */}
              <div className="border-t border-[#262626] pt-4">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FACC15] mb-3">Даты</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <DateField label="Дата документа" date={invitationForm.documentDate}
                    onDateChange={(d) => setInvitationForm(p => ({...p, documentDate: d}))} testId="inv-doc-date" />
                  <DateField label="Дата прибытия / Start" date={invitationForm.arrivalDate}
                    onDateChange={(d) => setInvitationForm(p => ({...p, arrivalDate: d}))} testId="inv-arrival-date" />
                  <DateField label="Окончание работы / End" date={invitationForm.workEndDate}
                    onDateChange={(d) => setInvitationForm(p => ({...p, workEndDate: d}))} testId="inv-end-date" />
                </div>
              </div>

              {/* Personal info section */}
              <div className="border-t border-[#262626] pt-4">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FACC15] mb-3">Личные данные</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField icon={User} label="Family Name" value={invitationForm.familyName}
                    onChange={(v) => setInvitationForm(p => ({...p, familyName: v}))} placeholder="KHAYDAROV" testId="inv-family-name" />
                  <FormField icon={User} label="Given Name(s)" value={invitationForm.givenName}
                    onChange={(v) => setInvitationForm(p => ({...p, givenName: v}))} placeholder="JAKHONGIR" testId="inv-given-name" />
                  <FormField icon={Globe} label="Nationality" value={invitationForm.nationality}
                    onChange={(v) => setInvitationForm(p => ({...p, nationality: v}))} placeholder="UZBEKISTAN" testId="inv-nationality" />
                  <FormField icon={MapPin} label="Place of Birth" value={invitationForm.placeOfBirth}
                    onChange={(v) => setInvitationForm(p => ({...p, placeOfBirth: v}))} placeholder="ANDIJAN REGION" testId="inv-pob" />
                  <FormField icon={CalendarBlank} label="Date of Birth (DD.MM.YYYY)" value={invitationForm.dateOfBirth}
                    onChange={(v) => setInvitationForm(p => ({...p, dateOfBirth: v}))} placeholder="08.10.1978" testId="inv-dob" />
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Gender</Label>
                    <Select value={invitationForm.gender} onValueChange={(v) => setInvitationForm(p => ({...p, gender: v}))}>
                      <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] rounded-none h-11 focus:border-[#FACC15] focus:ring-0" data-testid="inv-gender">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141414] border-[#262626] text-[#FAFAFA] rounded-none">
                        <SelectItem value="Male" className="focus:bg-[#FACC15]/10 rounded-none">Male</SelectItem>
                        <SelectItem value="Female" className="focus:bg-[#FACC15]/10 rounded-none">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Passport section */}
              <div className="border-t border-[#262626] pt-4">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FACC15] mb-3">Паспорт</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField icon={IdentificationCard} label="Passport Number" value={invitationForm.passportNumber}
                    onChange={(v) => setInvitationForm(p => ({...p, passportNumber: v}))} placeholder="FA2960310" testId="inv-passport" />
                  <FormField icon={CalendarBlank} label="Issue Date (DD.MM.YYYY)" value={invitationForm.passportIssueDate}
                    onChange={(v) => setInvitationForm(p => ({...p, passportIssueDate: v}))} placeholder="02.06.2021" testId="inv-passport-issue" />
                  <FormField icon={CalendarBlank} label="Expiry Date (DD.MM.YYYY)" value={invitationForm.passportExpiryDate}
                    onChange={(v) => setInvitationForm(p => ({...p, passportExpiryDate: v}))} placeholder="01.06.2031" testId="inv-passport-expiry" />
                  <FormField icon={MapPin} label="Place of Issue" value={invitationForm.passportPlaceOfIssue}
                    onChange={(v) => setInvitationForm(p => ({...p, passportPlaceOfIssue: v}))} placeholder="INTERNAL AFFAIRS" testId="inv-passport-place" />
                </div>
              </div>

              {/* Work section */}
              <div className="border-t border-[#262626] pt-4">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FACC15] mb-3">Работа</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField icon={Briefcase} label="Job Title" value={invitationForm.jobTitle}
                    onChange={(v) => setInvitationForm(p => ({...p, jobTitle: v}))} placeholder="Delivery Driver" testId="inv-job-title" />
                  <FormField icon={CurrencyEur} label="Salary (напр. 3600 €)" value={invitationForm.salary}
                    onChange={(v) => setInvitationForm(p => ({...p, salary: v}))} placeholder="3600 €" testId="inv-salary" />
                  <FormField icon={IdentificationCard} label="Employee Number" value={invitationForm.employeeNumber}
                    onChange={(v) => setInvitationForm(p => ({...p, employeeNumber: v}))} placeholder="AP 811073 Q" testId="inv-employee-num" />
                </div>
              </div>

              {/* Work Address */}
              <div className="border-t border-[#262626] pt-4">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FACC15] mb-3">Рабочий адрес</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField icon={MapPin} label="Address" value={invitationForm.workAddress}
                    onChange={(v) => setInvitationForm(p => ({...p, workAddress: v}))} placeholder="Charles-de-Gaulle-Straße 20" testId="inv-work-addr" />
                  <FormField icon={MapPin} label="City" value={invitationForm.workCity}
                    onChange={(v) => setInvitationForm(p => ({...p, workCity: v}))} placeholder="Bonn" testId="inv-work-city" />
                  <FormField icon={MapPin} label="County / Province" value={invitationForm.workCounty}
                    onChange={(v) => setInvitationForm(p => ({...p, workCounty: v}))} placeholder="WEST MIDLANDS" testId="inv-work-county" />
                  <FormField icon={MapPin} label="Postcode" value={invitationForm.workPostcode}
                    onChange={(v) => setInvitationForm(p => ({...p, workPostcode: v}))} placeholder="53113" testId="inv-work-postcode" />
                </div>
              </div>

              {/* Home Address */}
              <div className="border-t border-[#262626] pt-4">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3] mb-3">Домашний адрес (необязательно)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField icon={MapPin} label="Address" value={invitationForm.homeAddress}
                    onChange={(v) => setInvitationForm(p => ({...p, homeAddress: v}))} placeholder="-" testId="inv-home-addr" />
                  <FormField icon={MapPin} label="City" value={invitationForm.homeCity}
                    onChange={(v) => setInvitationForm(p => ({...p, homeCity: v}))} placeholder="-" testId="inv-home-city" />
                  <FormField icon={MapPin} label="Postcode" value={invitationForm.homePostcode}
                    onChange={(v) => setInvitationForm(p => ({...p, homePostcode: v}))} placeholder="-" testId="inv-home-postcode" />
                  <FormField icon={Globe} label="Country" value={invitationForm.homeCountry}
                    onChange={(v) => setInvitationForm(p => ({...p, homeCountry: v}))} placeholder="UZBEKISTAN" testId="inv-home-country" />
                </div>
              </div>

              {/* Contact Email */}
              {selectedVisaTemplateId && selectedVisaTemplateId !== 'none' && (
                <div className="border-t border-[#262626] pt-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FACC15] mb-3">Контактный Email (для визы)</p>
                  <FormField icon={EnvelopeSimple} label="Email" value={invitationForm.contactEmail}
                    onChange={(v) => setInvitationForm(p => ({...p, contactEmail: v}))} placeholder="example@gmail.com" testId="inv-contact-email" />
                </div>
              )}
            </>
          )}
        </div>
        {invitationTemplates.length > 0 && (
          <div className="p-4 border-t border-[#262626]">
            <Button onClick={onGenerate} disabled={isGeneratingInvitation || !selectedInvitationTemplateId}
              className="w-full h-12 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A0A0A] font-semibold rounded-none disabled:opacity-50"
              data-testid="generate-invitation-btn">
              {isGeneratingInvitation ? <><SpinnerGap size={20} className="animate-spin mr-2" />Генерация...</> :
                <><AirplaneTilt size={20} className="mr-2" weight="bold" />{selectedVisaTemplateId && selectedVisaTemplateId !== 'none' ? 'Создать приглашение + визу' : 'Создать приглашение'}</>}
            </Button>
          </div>
        )}
      </div>
    </div>
    <div className="space-y-4">
      <HistoryPanel title="Приглашения" count={invitations.length}>
        <HistoryList items={invitations} loading={loadingInvitations}
          onDownload={onDownloadInvitation} onDelete={onDeleteInvitation}
          nameField="full_name" subtitleField="sponsor_name" testPrefix="invitation-item" />
      </HistoryPanel>
      <HistoryPanel title="Визы" count={visas.length}>
        <HistoryList items={visas} loading={loadingInvitations}
          onDownload={onDownloadVisa} onDelete={onDeleteVisa}
          nameField="full_name" subtitleField="gwf_number" testPrefix="visa-item" />
      </HistoryPanel>
    </div>
  </div>
);
